import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
const Home = () => import("@/views/Home.vue");
const Hotels = () => import("@/views/Hotels/HotelList.vue");
const HotelDetails = () => import("@/views/Hotels/HotelDetails.vue");
const RoomDetails = () => import("@/views/Hotels/RoomDetails.vue");
const NotFound = () => import("@/views/404.vue");
const UserRegister = () => import("@/views/User/UserRegister.vue");
const EventRegister = () => import("@/views/User/EventRegister.vue");
const Register = () => import("@/views/User/Register.vue");
const Payment = () => import("@/views/User/Payment.vue");
const AccountSettings = () => import("@/views/User/AccountSettings.vue");
const Login = () => import("@/views/Login.vue");
const PasswordRecovery = () => import("@/views/User/PasswordRecovery.vue");
const PrivacyPolicy = () => import("@/components/home/PrivacyPolicy.vue");
const SpeakerDetail = () => import("@/components/speakers/SpeakerDetail.vue");
const RegistrationPolicies = () =>
  import("@/components/home/RegistrationPolicies.vue");
const BookingPolicies = () => import("@/components/home/BookingPolicies.vue");
const Abstracts = () => import("@/views/Abstracts.vue");
const HotelFilterList = () => import("@/views/Hotels/HotelFilterList.vue");
const MyBookings = () => import("@/views/Hotels/MyBookings.vue");
const NotFoundEvent = () => import("@/views/NotFoundEvent.vue");
const BookingsPayment = () => import("@/views/Hotels/BookingsPayment.vue");
const Expo = () => import("@/views/Expo.vue");
const Program = () => import("@/views/Program/Program.vue");
const UploadAbstract = () => import("@/views/Abstracts/UploadAbstract.vue");
const CoursePayment = () => import("@/views/Program/CoursePayment.vue");
const Courses = () => import("@/views/Program/Courses.vue");
const WelcomeMessage = () => import("@/views/WelcomeMessage/Message.vue");
const QRIdentification = () => import("@/views/User/QRIdentification.vue");
const ResetPassword = () => import("@/views/User/ResetPassword.vue");
const ControllerActions = () => import("@/views/User/ControllerActions.vue");
const EmailVerified = () => import("@/views/User/EmailVerified.vue");
const AppEvent = () => import("@/AppEvent.vue");

import store from "@/store";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/not-found-event",
  },
  {
    path: "/:eventID",
    component: AppEvent,
    children: [
      {
        path: "",
        name: "Home",
        component: Home,
      },
      {
        path: "user-register",
        name: "UserRegister",
        component: UserRegister,
        beforeEnter: (to, from, next) => {
          const basicAuthData = store.getters["user/data"];
          const logged = store.getters["user/isLogged"];
          const register = store.getters["user/isRegister"];
          const eventID = to.params.eventID;
          if (!basicAuthData) return next(`/${eventID}/register`);
          if (register && !logged) return next(`/${eventID}/event-register`);
          else next();
        },
      },
      {
        path: "register",
        name: "Register",
        beforeEnter: (to, from, next) => {
          const eventID = to.params.eventID;
          const basicAuthData = store.getters["user/data"];
          const register = store.getters["user/isRegister"];
          if (basicAuthData && !register)
            return next(`/${eventID}/user-register`);
          if (register) return next(`/${to.params.eventID}/event-register`);
          else next();
        },
        component: Register,
      },
      {
        path: "event-register",
        name: "EventRegister",
        component: EventRegister,
        beforeEnter: (to, from, next) => {
          const basicAuthData = store.getters["user/data"];
          const register = store.getters["user/isRegister"];
          const eventID = to.params.eventID;
          if (!basicAuthData) return next(`/${eventID}/register`);
          else if (!register) return next(`/${eventID}/user-register`);
          else next();
        },
      },
      {
        path: "checkout",
        name: "Checkout",
        component: Payment,
        beforeEnter: (to, from, next) => {
          const eventID = to.params.eventID;
          const register = store.getters["user/isRegister"];
          const logged = store.getters["user/isLogged"];
          if (!register) return next(`/${eventID}/user-register`);
          if (register && !logged) return next(`/${eventID}/event-register`);
          else next();
        },
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: "abstracts",
        name: "Abstracts",
        component: Abstracts,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: "password-recovery",
        name: "PasswordRecovery",
        component: PasswordRecovery,
      },
      {
        path: "account-settings",
        name: "AccountSettings",
        component: AccountSettings,
        meta: {
          requiresAuth: true,
          requiresPay: true,
        },
        beforeEnter: (to, from, next) => {
          const eventID = to.params.eventID;
          const register = store.getters["user/isRegister"];
          const logged = store.getters["user/isLogged"];
          if (!register) return next(`/${eventID}/user-register`);
          if (register && !logged) return next(`/${eventID}/event-register`);
          else next();
        },
      },
      {
        path: "privacy-policy",
        name: "PrivacyPolicy",
        component: PrivacyPolicy,
      },
      {
        path: "registration-policies",
        name: "RegistrationPolicies",
        component: RegistrationPolicies,
      },
      {
        path: "booking-policies",
        name: "BookingPolicies",
        component: BookingPolicies,
      },
      {
        path: "hotels",
        name: "Hotels",
        component: Hotels,
        props: true,
      },
      {
        path: "speakers",
        name: "speakers",
        component: SpeakerDetail,
        props: true,
      },
      {
        path: "hotels/:hotelID",
        name: "HotelDetails",
        component: HotelDetails,
      },
      {
        path: "hotels/:hotelID/search",
        name: "HotelFilterList",
        component: HotelFilterList,
      },
      {
        path: "hotels/:hotelID/room/:roomID",
        name: "RoomDetails",
        component: RoomDetails,
      },
      {
        path: "hotels/:hotelID/bookings-checkout",
        name: "BookingsCheckout",
        component: BookingsPayment,
        beforeEnter: (to, from, next) => {
          const eventID = to.params.eventID;
          const register = store.getters["user/isRegister"];
          const logged = store.getters["user/isLogged"];
          if (!register) return next(`/${eventID}/user-register`);
          if (register && !logged) return next(`/${eventID}/event-register`);
          else next();
        },
        meta: {
          requiresAuth: true,
          requiresPay: true,
        },
      },
      {
        path: "expo",
        name: "Expo",
        component: Expo,
      },
      {
        path: "uploadAbstract",
        name: "UploadAbstract",
        component: UploadAbstract,
        beforeEnter: (to, from, next) => {
          const eventID = to.params.eventID;
          const register = store.getters["user/isRegister"];
          const logged = store.getters["user/isLogged"];
          if (!register) return next(`/${eventID}/user-register`);
          if (register && !logged) return next(`/${eventID}/event-register`);
          else next();
        },
        meta: {
          requiresAuth: true,
          requiresPay: true,
        },
      },
      {
        path: "login",
        name: "Login",
        component: Login,
        beforeEnter: (to, from, next) => {
          const basicAuthData = store.getters["user/data"];
          const logged = store.getters["user/isLogged"];
          const register = store.getters["user/isRegister"];
          if (basicAuthData && !logged) {
            if (register) return next(`/${to.params.eventID}/event-register`);
            return next(`/${to.params.eventID}/user-register`);
          } else next();
        },
      },
      {
        path: "my-bookings",
        name: "MyBookings",
        component: MyBookings,
        beforeEnter: (to, from, next) => {
          const eventID = to.params.eventID;
          const register = store.getters["user/isRegister"];
          const logged = store.getters["user/isLogged"];
          if (!register) return next(`/${eventID}/user-register`);
          if (register && !logged) return next(`/${eventID}/event-register`);
          else next();
        },
        meta: {
          requiresAuth: true,
          requiresPay: true,
        },
      },
      {
        path: "event-identification",
        name: "QRIdentification",
        component: QRIdentification,
        beforeEnter: (to, from, next) => {
          const eventID = to.params.eventID;
          const register = store.getters["user/isRegister"];
          const logged = store.getters["user/isLogged"];
          if (!register) return next(`/${eventID}/user-register`);
          if (register && !logged) return next(`/${eventID}/event-register`);
          else next();
        },
        meta: {
          requiresAuth: true,
          requiresPay: true,
        },
      },
      {
        path: "program",
        name: "Program",
        component: Program,
      },
      {
        path: "course-checkout",
        name: "CourseCheckout",
        component: CoursePayment,
        beforeEnter: (to, from, next) => {
          const eventID = to.params.eventID;
          const register = store.getters["user/isRegister"];
          const logged = store.getters["user/isLogged"];
          if (!register) return next(`/${eventID}/user-register`);
          if (register && !logged) return next(`/${eventID}/event-register`);
          else next();
        },
        meta: {
          requiresAuth: true,
          requiresPay: true,
        },
      },
      {
        path: "courses",
        name: "Courses",
        component: Courses,
        beforeEnter: (to, from, next) => {
          const eventID = to.params.eventID;
          const register = store.getters["user/isRegister"];
          const logged = store.getters["user/isLogged"];
          if (!register) return next(`/${eventID}/user-register`);
          if (register && !logged) return next(`/${eventID}/event-register`);
          else next();
        },
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: "welcome-message",
        name: "WelcomeMessage",
        component: WelcomeMessage,
      },
    ],
  },
  {
    path: "/controller-actions",
    name: "ControllerActions",
    component: ControllerActions,
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: ResetPassword,
  },
  {
    path: "/email-verified",
    name: "EmailVerified",
    component: EmailVerified,
  },
  {
    path: "/not-found-event",
    name: "NotFoundEvent",
    component: NotFoundEvent,
  },
  {
    path: "/:catchAll(.*)",
    name: "NotFound",
    component: NotFound,
    meta: {
      requiresAuth: false,
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});
router.beforeEach(async (to: any, from, next) => {
  try {
    await store.dispatch("user/getAuthState", to.params.eventID);
  } catch {
    //no user logged in
  }
  const requiresAuth = to.matched.some(({ meta }: any) => meta.requiresAuth);
  const requiresPay = to.matched.some(({ meta }: any) => meta.requiresPay);
  const logged = store.getters["user/isLogged"];
  const basicAuthData = store.getters["user/data"];
  const register = store.getters["user/isRegister"];
  const reRouteAfterLogin = [
    "Login",
    "UserRegister",
    "EventRegister",
    "Register",
  ];
  const statusPaid = store.getters["user/isPaid"];
  if (requiresAuth && !logged) {
    if (register && !logged && basicAuthData)
      return next(`/${to.params.eventID}/event-register`);
    else if (!register && !logged && basicAuthData)
      return next(`/${to.params.eventID}/user-register`);
    else return next(`/${to.params.eventID}/login`);
  } else if (logged && reRouteAfterLogin.includes(to.name)) {
    return next(`/${to.params.eventID}`);
  } else if (logged && requiresPay && !statusPaid) {
    return next(`/${to.params.eventID}`);
  }
  if (to.name === "Home") {
    return next(`/${to.params.eventID}/hotels`);
  } else {
    next();
  }
});
export default router;
